// @flow strict
import React from 'react';
import { graphql } from 'gatsby';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import Feed from '../components/Feed';
import Page from '../components/Page';
import Pagination from '../components/Pagination';
import layoutStyles from '../components/Layout/Layout.module.scss';
import { useSiteMetadata } from '../hooks';
import type { PageContext, AllMarkdownRemark } from '../types';

const DESCRIPTION = 'The blog as it looked before the constellation became the landing page.';

type Props = {
  data: AllMarkdownRemark,
  pageContext: PageContext
};

/**
 * A deliberate snapshot of the old landing page: the original sidebar, the
 * list of articles, and the same pagination. It keeps using the Sidebar
 * component the rest of the site has moved away from, which is the point —
 * this page should not follow the new design.
 */
const PreviousViewTemplate = ({ data, pageContext }: Props) => {
  const { title: siteTitle } = useSiteMetadata();
  const {
    currentPage, hasNextPage, hasPrevPage, prevPagePath, nextPagePath
  } = pageContext;

  const { edges } = data.allMarkdownRemark;
  const pageTitle = currentPage > 0
    ? `Blog's Previous View - Page ${currentPage} - ${siteTitle}`
    : `Blog's Previous View - ${siteTitle}`;

  return (
    <Layout title={pageTitle} description={DESCRIPTION} className={layoutStyles['layout--legacy']}>
      <Sidebar isIndex />
      <Page>
        <Feed edges={edges} />
        <Pagination
          prevPagePath={prevPagePath}
          nextPagePath={nextPagePath}
          hasPrevPage={hasPrevPage}
          hasNextPage={hasNextPage}
        />
      </Page>
    </Layout>
  );
};

export const query = graphql`
  query PreviousViewTemplate($postsLimit: Int!, $postsOffset: Int!) {
    allMarkdownRemark(
      limit: $postsLimit,
      skip: $postsOffset,
      filter: { frontmatter: { template: { eq: "post" }, poetry: { ne: true }, reflections: { ne: true }, photograph: { ne: true }, draft: { ne: true } } },
      sort: { order: DESC, fields: [frontmatter___date] }
    ) {
      edges {
        node {
          fields { slug categorySlug }
          frontmatter { title date category description }
        }
      }
    }
  }
`;

export default PreviousViewTemplate;
